import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Newspaper, ChevronRight, Calendar, Tag, ArrowLeft, Loader2, Megaphone, Share2, Filter, X,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InfoHero = ({ title, subtitle, breadcrumb, icon: Icon }) => {
  const { language } = useLanguage();
  return (
    <div className="bg-gradient-to-br from-[#0F4C5C] to-[#0B3A47] text-white py-14">
      <div className="container-main">
        <nav className="flex items-center gap-2 text-sm text-white/70 mb-3 flex-wrap">
          <Link to="/" className="hover:text-white">{language === 'fr' ? 'Accueil' : 'Home'}</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/press" className="hover:text-white">{language === 'fr' ? 'Presse' : 'Press'}</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-[#E9C46A]">{breadcrumb}</span>
        </nav>
        <h1 className="text-3xl md:text-5xl font-bold tracking-tight flex items-center gap-3">
          {Icon && <Icon className="w-10 h-10 text-[#E9C46A]" />}
          {title}
        </h1>
        {subtitle && <p className="text-white/70 mt-3 max-w-2xl text-lg">{subtitle}</p>}
      </div>
    </div>
  );
};

const formatDate = (iso, lang) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', {
      day: '2-digit', month: 'long', year: 'numeric',
    });
  } catch { return iso; }
};

// ==========================================================================
// List page — /press/releases
// ==========================================================================
export const PressReleasesListPage = () => {
  const { language } = useLanguage();
  const T = (fr, en) => (language === 'fr' ? fr : en);
  const [searchParams, setSearchParams] = useSearchParams();
  const [releases, setReleases] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);

  const activeTag = searchParams.get('tag') || null;
  const fromDate = searchParams.get('from_date') || '';
  const toDate = searchParams.get('to_date') || '';

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    setSearchParams(next, { replace: false });
  };

  const reload = async () => {
    setLoading(true);
    const params = new URLSearchParams({ lang: language });
    if (activeTag) params.set('tag', activeTag);
    if (fromDate) params.set('from_date', fromDate);
    if (toDate) params.set('to_date', toDate);
    try {
      const { data } = await axios.get(`${API}/press/releases?${params.toString()}`);
      setReleases(data || []);
    } catch (err) {
      // noop
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload();  }, [language, activeTag, fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/press/tags`);
        setTags(data || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const clearFilters = () => setSearchParams({}, { replace: false });
  const hasFilters = activeTag || fromDate || toDate;

  return (
    <div className="min-h-screen bg-[#FBF8F3]" data-testid="press-releases-list-page">
      <InfoHero
        title={T('Communiqués de presse', 'Press releases')}
        subtitle={T(
          'Toutes nos annonces et actualités, dans l\'ordre du plus récent au plus ancien.',
          'All our announcements and news, newest first.',
        )}
        breadcrumb={T('Communiqués', 'Releases')}
        icon={Newspaper}
      />

      <div className="container-main py-12">
        {/* Filters */}
        <Card className="mb-6 border-[#0F4C5C]/10" data-testid="press-releases-filters">
          <CardContent className="p-4 md:p-5">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-4 h-4 text-[#0F4C5C]" />
              <h3 className="font-semibold text-[#0F4C5C] text-sm">{T('Filtres', 'Filters')}</h3>
              {hasFilters && (
                <Button size="sm" variant="ghost" onClick={clearFilters} className="ml-auto text-xs h-7" data-testid="press-clear-filters">
                  <X className="w-3 h-3 mr-1" /> {T('Réinitialiser', 'Reset')}
                </Button>
              )}
            </div>

            {/* Tag filter */}
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {tags.map((t) => (
                  <button
                    key={t.tag}
                    type="button"
                    onClick={() => updateParam('tag', activeTag === t.tag ? null : t.tag)}
                    data-testid={`press-tag-${t.tag}`}
                    className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                      activeTag === t.tag
                        ? 'bg-[#0F4C5C] text-white border-[#0F4C5C]'
                        : 'bg-white text-[#0F4C5C] border-[#0F4C5C]/20 hover:border-[#0F4C5C]'
                    }`}
                  >
                    #{t.tag} <span className="opacity-60 ml-0.5">({t.count})</span>
                  </button>
                ))}
              </div>
            )}

            {/* Date range */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md">
              <div>
                <label className="text-xs text-[#0F4C5C]/60 block mb-1">{T('À partir du', 'From')}</label>
                <Input type="date" value={fromDate} onChange={(e) => updateParam('from_date', e.target.value)} className="h-9" data-testid="press-from-date" />
              </div>
              <div>
                <label className="text-xs text-[#0F4C5C]/60 block mb-1">{T('Jusqu\'au', 'To')}</label>
                <Input type="date" value={toDate} onChange={(e) => updateParam('to_date', e.target.value)} className="h-9" data-testid="press-to-date" />
              </div>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#0F4C5C]" /></div>
        ) : releases.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-[#0F4C5C]/10" data-testid="press-releases-empty">
            <Megaphone className="w-14 h-14 mx-auto text-[#0F4C5C]/30 mb-3" />
            <p className="text-[#0F4C5C]/70 mb-2">{T('Aucun communiqué ne correspond aux filtres.', 'No releases match the filters.')}</p>
            {hasFilters && (
              <Button size="sm" variant="outline" onClick={clearFilters}>
                {T('Réinitialiser les filtres', 'Reset filters')}
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-6 max-w-4xl mx-auto">
            {releases.map((r) => (
              <Link
                key={r.id}
                to={`/press/releases/${r.slug}`}
                className="block bg-white rounded-2xl border border-[#0F4C5C]/10 hover:border-[#E9C46A] hover:shadow-lg transition-all overflow-hidden"
                data-testid={`press-release-card-${r.slug}`}
              >
                <div className="md:flex">
                  {r.hero_image_url && (
                    <div className="md:w-1/3 h-48 md:h-auto bg-[#0F4C5C]/5">
                      <img src={r.hero_image_url} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-6 md:p-8 flex-1">
                    <div className="flex items-center gap-3 text-xs text-[#0F4C5C]/60 mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(r.publish_date || r.created_at, language)}
                      </span>
                      {(r.tags || []).slice(0, 2).map((tag, i) => (
                        <span key={i} className="flex items-center gap-1">
                          <Tag className="w-3 h-3" />{tag}
                        </span>
                      ))}
                    </div>
                    <h2 className="text-xl md:text-2xl font-bold text-[#0F4C5C] mb-2 hover:text-[#2A9D8F] transition-colors">
                      {language === 'fr' ? r.title_fr : (r.title_en || r.title_fr)}
                    </h2>
                    <p className="text-sm text-[#0F4C5C]/70 leading-relaxed">
                      {language === 'fr' ? (r.summary_fr || '') : (r.summary_en || r.summary_fr || '')}
                    </p>
                    <p className="text-sm text-[#2A9D8F] font-semibold mt-3 flex items-center gap-1">
                      {T('Lire le communiqué', 'Read release')}
                      <ChevronRight className="w-4 h-4" />
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ==========================================================================
// Detail page — /press/releases/:slug
// ==========================================================================
export const PressReleaseDetailPage = () => {
  const { language } = useLanguage();
  const T = (fr, en) => (language === 'fr' ? fr : en);
  const { slug } = useParams();
  const [release, setRelease] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/press/releases/${slug}`);
        setRelease(data);
      } catch (err) {
        // noop
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FBF8F3]">
      <Loader2 className="w-10 h-10 animate-spin text-[#0F4C5C]" />
    </div>
  );

  if (!release) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FBF8F3]" data-testid="press-release-not-found">
      <div className="text-center max-w-md">
        <Megaphone className="w-14 h-14 mx-auto text-[#0F4C5C]/30 mb-3" />
        <h1 className="text-2xl font-bold text-[#0F4C5C]">{T('Communiqué introuvable', 'Release not found')}</h1>
        <Link to="/press/releases">
          <Button variant="outline" className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {T('Tous les communiqués', 'All releases')}
          </Button>
        </Link>
      </div>
    </div>
  );

  const title = language === 'fr' ? release.title_fr : (release.title_en || release.title_fr);
  const body = language === 'fr' ? release.body_fr : (release.body_en || release.body_fr);

  const share = () => {
    if (navigator.share) {
      navigator.share({ title, url: window.location.href }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF8F3]" data-testid="press-release-detail-page">
      <InfoHero
        title={title}
        breadcrumb={(release.publish_date || '').slice(0, 10)}
        icon={Newspaper}
      />

      <div className="container-main py-12">
        <article className="max-w-3xl mx-auto bg-white rounded-2xl border border-[#0F4C5C]/10 overflow-hidden">
          {release.hero_image_url && (
            <div className="h-64 md:h-80 bg-[#0F4C5C]/5">
              <img src={release.hero_image_url} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          <div className="p-6 md:p-10">
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-[#0F4C5C]/10">
              <div className="flex items-center gap-3 text-sm text-[#0F4C5C]/60">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(release.publish_date || release.created_at, language)}</span>
                {(release.tags || []).length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {release.tags.slice(0, 3).map((tag) => (
                      <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                    ))}
                  </div>
                )}
              </div>
              <Button size="sm" variant="ghost" onClick={share} data-testid="share-release-btn">
                <Share2 className="w-4 h-4 mr-1" />
                {T('Partager', 'Share')}
              </Button>
            </div>

            {(language === 'fr' ? release.summary_fr : (release.summary_en || release.summary_fr)) && (
              <p className="text-lg text-[#0F4C5C] font-medium leading-relaxed mb-6 italic">
                {language === 'fr' ? release.summary_fr : (release.summary_en || release.summary_fr)}
              </p>
            )}

            <div className="prose prose-stone max-w-none text-[#0F4C5C]/90 leading-relaxed">
              {body.split('\n\n').map((para, i) => (
                <p key={i} className="mb-4">{para}</p>
              ))}
            </div>

            <div className="mt-10 pt-6 border-t border-[#0F4C5C]/10">
              <Link to="/press/releases">
                <Button variant="outline" data-testid="back-to-releases">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {T('Tous les communiqués', 'All releases')}
                </Button>
              </Link>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};
